import { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Popover,
  Box,
  Typography,
  TextField,
  Grid,
  Dialog,
  Checkbox,
  Autocomplete,
  Menu,
  Divider,
  MenuItem,
  Chip,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Close as CloseIcon } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import {
  Edit,
  Delete as DeleteIcon,
  Print,
  Visibility,
  Description as FileIcon,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  SalesOrderListApi,
  SalesOrderDelete,
  ApproveSo,
  DivisionList,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast, ToastContainer } from "react-toastify";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import config from "../../../config";
import { generatePdf } from "./SalesOrderPrint";
import { SalesListExport } from "../../../Services/Services";

pdfMake.vfs = pdfFonts.vfs;

const SalesOrderPreview = ({ order, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Helper to safely format numbers
  const formatCurrency = (amount) => {
    return amount
      ? Number(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      : "0.00";
  };

  // Border style for the document look
  const borderStyle = "1px solid #000";
  const stored = localStorage.getItem("selectedDivision");
  const divisionLocalStorage = JSON.parse(stored);
  // Calculate subtotal (total before tax)
  const calculateSubtotal = () => {
    if (!order.items || order.items.length === 0) return 0;
    return order.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  };

  // Calculate total tax (assuming tax is included in amount)
  const calculateTotalTax = () => {
    if (!order.items || order.items.length === 0) return 0;
    return order.items.reduce((sum, item) => {
      const baseAmount = Number(item.quantity || 0) * Number(item.rate || 0);
      const discountAmount = baseAmount * (Number(item.dis_per || 0) / 100);
      const amountAfterDiscount = baseAmount - discountAmount;
      const taxAmount = Number(item.amount || 0) - amountAfterDiscount;
      return sum + taxAmount;
    }, 0);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: isMobile ? "100%" : isTablet ? "700px" : "900px",
        border: borderStyle,
        fontFamily: "Arial, sans-serif",
        bgcolor: "white",
        fontSize: isMobile ? "10px" : "12px",
        color: "#000",
        mx: "auto",
        position: "relative",
      }}
    >
      {/* 1. MAIN TITLE */}
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          borderBottom: borderStyle,
          py: isMobile ? 0.3 : 0.5,
          fontSize: isMobile ? "0.9rem" : "1rem",
        }}
      >
        SALES ORDER
      </Typography>

      {/* 2. HEADER INFO GRID (Responsive Columns) */}
      <Grid container sx={{ borderBottom: borderStyle }}>
        {/* Column 1: Supplier */}
        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            p: isMobile ? 0.8 : 1,
            borderRight: { xs: "none", sm: borderStyle },
            borderBottom: { xs: borderStyle, sm: "none" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.65rem" : "0.75rem",
            }}
          >
            Supplier:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            }}
          >
            CRANOIST MHE PVT. LTD.
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: isMobile ? "0.6rem" : "0.7rem", lineHeight: 1.3 }}
          >
            Plot No.-37, Phase-2, Siltara Industrial Area, Raipur - 493111
            <br />
            Email: info@cranoistmhe.com
            <br />
            GSTIN: {divisionLocalStorage?.gst_no || "22AAHFK2314C1Z4"}
            <br />
            Contact:  9825586999
          </Typography>
        </Grid>

        {/* Column 2: Project/SO Info */}
        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            p: isMobile ? 0.8 : 1,
            borderRight: { xs: "none", sm: borderStyle },
            borderBottom: { xs: borderStyle, sm: "none" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, mb: 0.3, flexWrap: "wrap" }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
            >
              Project:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: isMobile ? "0.6rem" : "0.7rem",
                wordBreak: "break-word",
              }}
            >
              {order.project_name || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, mb: 0.3 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
            >
              S.O. No.:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.6rem" : "0.7rem" }}
            >
              {order.so_no}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, mb: 0.3 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
            >
              S.O. Date:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.6rem" : "0.7rem" }}
            >
              {new Date(order.order_date).toLocaleDateString("en-GB")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, mb: 0.3 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
            >
              P.O. No.:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: isMobile ? "0.6rem" : "0.7rem",
                wordBreak: "break-word",
              }}
            >
              {order.po_number}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
            >
              P.O. Date:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.6rem" : "0.7rem" }}
            >
              {order.order_date
                ? new Date(order.order_date).toLocaleDateString("en-GB")
                : "N/A"}
            </Typography>
          </Box>
        </Grid>

        {/* Column 3: Buyer */}
        <Grid item xs={12} sm={4} sx={{ p: isMobile ? 0.8 : 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.65rem" : "0.75rem",
            }}
          >
            Buyer:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            }}
          >
            {order.party_details?.company_name || "N/A"}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: isMobile ? "0.6rem" : "0.7rem", lineHeight: 1.3 }}
          >
            Contact: {order.party_details?.contact || "N/A"}
            <br />
            GST: {order.party_details?.gst || "N/A"}
            <br />
            Email: {order.party_details?.email || "N/A"}
            <br />
            Address: {order.party_details?.address || "N/A"}
          </Typography>
        </Grid>
      </Grid>

      {/* 3. ITEMS TABLE - Responsive Version */}
      <Box sx={{ borderBottom: borderStyle, overflowX: "auto" }}>
        {isMobile ? (
          // Mobile Card View for Items - WITH BETTER BORDERS
          <Box>
            {(order.items?.length > 0 ? order.items : []).map((item, index) => (
              <Box
                key={item.id || index}
                sx={{
                  p: 1,
                  borderBottom: borderStyle,
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#fff",
                }}
              >
                {/* Item Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.8,
                    pb: 0.5,
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ fontSize: "0.75rem", color: "#1976d2" }}
                  >
                    {index + 1}. {item.item_name || "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ fontSize: "0.75rem", color: "#2e7d32" }}
                  >
                    ₹{formatCurrency(item.amount)}
                  </Typography>
                </Box>

                {/* Description */}
                {item.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: "#666",
                      display: "block",
                      mb: 0.8,
                      fontStyle: "italic",
                      pb: 0.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    {item.description}
                  </Typography>
                )}

                {/* Item Details Grid */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  {/* Category Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pb: 0.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                    >
                      Category:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                      {item.category_name || "-"}
                    </Typography>
                  </Box>

                  {/* Sub-Category Row */}
                  {/* <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pb: 0.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                    >
                      Sub-Category:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                      {item.sub_category_name || "-"}
                    </Typography>
                  </Box> */}

                  {/* Quantity Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pb: 0.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                    >
                      Quantity:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                      {item.quantity || 0} {item.uom || "Nos"}
                    </Typography>
                  </Box>

                  {/* Rate Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pb: 0.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                    >
                      Rate:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                      ₹{formatCurrency(item.rate)}
                    </Typography>
                  </Box>

                  {/* Discount Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pb: 0.5,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                    >
                      Discount:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                      {item.dis_per || 0}%
                    </Typography>
                  </Box>

                  {/* Base Amount Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pb: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                    >
                      Base Amount:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                      ₹
                      {formatCurrency(
                        Number(item.quantity || 0) * Number(item.rate || 0)
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          // Desktop Table View
          <Table
            size="small"
            sx={{
              borderCollapse: "collapse",
              "& td, & th": {
                border: borderStyle,
                borderRight: borderStyle,
                borderBottom: borderStyle,
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  borderBottom: borderStyle,
                  height: "30px",
                  "& th": {
                    borderBottom: borderStyle,
                  },
                }}
              >
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  S.No.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Category
                </TableCell>
                {/* <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Sub-Category
                </TableCell> */}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    width: "30%",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Item Description
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  UOM
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Qty
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Rate
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Disc%
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "4px",
                    fontSize: isTablet ? "0.65rem" : "0.75rem",
                  }}
                >
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(order.items?.length > 0 ? order.items : []).map(
                (item, index) => (
                  <TableRow
                    key={item.id || index}
                    sx={{
                      height: "30px",
                      "& td": {
                        borderBottom: borderStyle,
                      },
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {item.category_name || "-"}
                    </TableCell>
                    {/* <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {item.sub_category_name || "-"}
                    </TableCell> */}
                    <TableCell sx={{ padding: "4px" }}>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ fontSize: isTablet ? "0.65rem" : "0.7rem" }}
                      >
                        {item.item_name || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontSize: isTablet ? "0.6rem" : "0.65rem" }}
                      >
                        {item.description || ""}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {item.uom || "Nos"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {item.quantity || 0}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {formatCurrency(item.rate)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {item.dis_per || 0}%
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {formatCurrency(
                        Number(item.quantity || 0) * Number(item.rate || 0)
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px",
                        fontSize: isTablet ? "0.65rem" : "0.7rem",
                      }}
                    >
                      {formatCurrency(item.amount)}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* 4. FOOTER & TOTALS - Responsive */}
      <Grid container>
        {/* Left Side: Terms & Conditions */}
        <Grid
          item
          xs={12}
          sm={8}
          sx={{
            p: isMobile ? 1.5 : 2,
            borderRight: { xs: "none", sm: borderStyle },
            borderBottom: { xs: borderStyle, sm: "none" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              mb: 1,
              fontSize: isMobile ? "0.7rem" : "0.75rem",
            }}
          >
            Terms & Conditions:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {order.terms_and_conditions?.map((term, index) => (
              <Typography
                key={index}
                variant="caption"
                sx={{ fontSize: isMobile ? "0.6rem" : "10px", lineHeight: 1.4 }}
              >
                <strong>{term.key}:</strong> {term.value}
              </Typography>
            ))}
          </Box>
          {order.remarks && (
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                fontStyle: "italic",
                fontSize: isMobile ? "0.65rem" : "0.7rem",
              }}
            >
              <strong>Remarks:</strong> {order.remarks}
            </Typography>
          )}
        </Grid>

        {/* Right Side: Calculation Breakdown */}
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: isMobile ? 0.8 : 0.5,
              borderBottom: borderStyle,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              Subtotal (Rs.)
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              {formatCurrency(calculateSubtotal())}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: isMobile ? 0.8 : 0.5,
              borderBottom: borderStyle,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              CGST ({order.cgst_per || 0}%)
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              {formatCurrency(
                (calculateSubtotal() * Number(order.cgst_per || 0)) / 100
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: isMobile ? 0.8 : 0.5,
              borderBottom: borderStyle,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              SGST ({order.sgst_per || 0}%)
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              {formatCurrency(
                (calculateSubtotal() * Number(order.sgst_per || 0)) / 100
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: isMobile ? 0.8 : 0.5,
              borderBottom: borderStyle,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              Round Off
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
            >
              {formatCurrency(order.round_off)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: isMobile ? 0.8 : 0.5,
              bgcolor: "#f0f0f0",
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
            >
              Total (Rs.)
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
            >
              {formatCurrency(order.grand_total)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default function SalesOrderTable() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [anchorEl, setAnchorEl] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [salesOrders, setSalesOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const baseURL = config.staticBaseURL;
  const [soToApprove, setSoToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [loading, setLoading] = useState(false); // For scroll pagination
  const [filterLoading, setFilterLoading] = useState(false);
  const [showRemarkForOrderId, setShowRemarkForOrderId] = useState(null);
  const userRole = localStorage.getItem("role");
  const [divisionLists, setDivisionLists] = useState([]);
  const [expandedSO, setExpandedSO] = useState(null);
  const [isExcelLoading, setExcelLoading] = useState(false);
  const [filters, setFilters] = useState({
    project_name: "",
    party: "",
    division: "",
    date_range: "this_month",
    po_date_from: "",
    po_date_to: "",
  });

  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [selectingDate, setSelectingDate] = useState(null);

  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });

  const [poDateMenuAnchor, setPoDateMenuAnchor] = useState(null);
  const [poDateRange, setPoDateRange] = useState({ from: "", to: "" });
  const [tempPoDateRange, setTempPoDateRange] = useState({
    from: "",
    to: "",
  });

  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  const handleExcelExport = async () => {
    if (salesOrders.length === 0) {
      toast.warning("No data available to export");
      return;
    }

    setExcelLoading(true);
    try {
      // Build params exactly like fetchOrders
      const params = { ...filters };

      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      if (filters.po_date_from && filters.po_date_to) {
        params.po_date_from = filters.po_date_from;
        params.po_date_to = filters.po_date_to;
      }

      console.log("Export Params:", params);

      const blob = await SalesListExport(params);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sales_Orders_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchOrders(1, true, filters);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange, poDateRange]);

  useEffect(() => {
    const fetchData = async () => {
      const divisions = await DivisionList();
      setDivisionLists(divisions || []);
    };
    fetchData();
  }, []);

  const fetchOrders = async (
    pageNum = 1,
    reset = false,
    filterValues = filters
  ) => {
    try {
      // ✅ Use filterLoading for reset/filter changes, loading for scroll
      if (reset) {
        setFilterLoading(true);
      } else {
        setLoading(true);
      }

      const params = { ...filterValues };

      if (
        filterValues.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      if (filterValues.po_date_from && filterValues.po_date_to) {
        params.po_date_from = filterValues.po_date_from;
        params.po_date_to = filterValues.po_date_to;
      }
      console.log("API Params being sent:", params);

      const data = await SalesOrderListApi(pageNum, params);

      if (reset || pageNum === 1) {
        setSalesOrders(data.results);
      } else {
        setSalesOrders((prev) => [...prev, ...data.results]);
      }

      setHasMore(data.next !== null);
      setPage(pageNum);
    } catch (error) {
      console.error("❌ Failed to fetch sales orders:", error);
      toast.error("Failed to load sales orders");
    } finally {
      // ✅ Reset the appropriate loading state
      setFilterLoading(false);
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchOrders(nextPage);
  };

  const handleClick = () => {
    navigate("/dashboard/saleorder");
  };

  const handleReset = () => {
    setFilters({
      project_name: "",
      party: "",
      division: "",
      date_range: "this_month",
      po_date_from: "",
      po_date_to: "",
    });
    setCustomDateRange({ from: "", to: "" });
    setSelectingDate(null);
    setSalesOrders([]);
    setPage(1);
    setHasMore(true);
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setPoDateRange({ from: "", to: "" });
    setTempPoDateRange({ from: "", to: "" });
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };
  const handleDelete = async (id) => {
    try {
      const response = await SalesOrderDelete(id);

      if (response) {
        const updated = salesOrders.filter((s) => s.id !== id);
        setSalesOrders(updated);
        toast.success("Sales order deleted successfully");
      } else {
        console.warn("Failed to delete from API");
      }
    } catch (error) {
      console.error("Error deleting sales order:", error);
      toast.error("Failed to delete sales order");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const handleApprove = async (order, status, remarks = "") => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveSo(
        order.id,
        approvedById,
        order.status,
        remarkInput
      );

      setSalesOrders((prev) =>
        prev.map((item) => (item.id === order.id ? updated : item))
      );

      toast.success(
        updated.status === "Approved"
          ? "SO approved successfully!"
          : "SO rejected successfully!"
      );
    } catch (error) {
      console.error("Approval Toggle Error:", error);
      toast.error("Failed to toggle SO approval.");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  function formatOnlyDate(dateTimeString, format = "dd-mm-yyyy") {
    const date = new Date(dateTimeString);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    if (format === "dd-mm-yyyy") {
      return `${day}-${month}-${year}`;
    } else if (format === "yyyy-mm-dd") {
      return `${year}-${month}-${day}`;
    } else {
      throw new Error("Unsupported format. Use 'dd-mm-yyyy' or 'yyyy-mm-dd'.");
    }
  }

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  // Handle Apply
  const handleApplyPoDateRange = () => {
    setPoDateRange(tempPoDateRange);
    setFilters((prev) => ({
      ...prev,
      po_date_from: tempPoDateRange.from,
      po_date_to: tempPoDateRange.to,
      date_range: "all",
    }));
    setPoDateMenuAnchor(null);
  };

  // Handle Cancel
  const handleCancelPoDateRange = () => {
    setTempPoDateRange(poDateRange);
    setPoDateMenuAnchor(null);
  };

  // Handle Clear
  const handleClearPoDateRange = () => {
    setPoDateRange({ from: "", to: "" });
    setTempPoDateRange({ from: "", to: "" });
    setFilters((prev) => ({
      ...prev,
      po_date_from: "",
      po_date_to: "",
    }));
    setPoDateMenuAnchor(null);
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);
  // 🎨 Compact Mobile Card Component (REPLACE EXISTING)
  const MobileSalesOrderCard = ({ order, index }) => {
    const isExpanded = expandedSO === order.id;

    return (
      <Box key={order.id} >
        {/* Main Row - Always Visible */}
        <Box
          onClick={() =>
            setExpandedSO(expandedSO === order.id ? null : order.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: `4px solid ${order.status === "Approved"
                ? "#4caf50"
                : order.status === "Rejected"
                  ? "#f44336"
                  : "#ff9800"
              }`,
            mt: index === 0 ? { xs: 4, sm: 2 } : 0
          }}
        >
          <Box sx={{ flex: 1 }}>
            {/* SO Number & Status */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "#666",
                  minWidth: 25,
                }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  flex: 1,
                }}
              >
                {order.so_no}
              </Typography>
            </Box>

            {/* Debtor & Date */}
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {order.party_details?.company_name?.substring(0, 20) || "N/A"}
              </span>
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>
                {new Date(order.order_date).toLocaleDateString("en-GB")}
              </span>
            </Typography>
          </Box>

          {/* Expand Icon */}
          <IconButton size="small" sx={{ ml: 1 }}>
            {expandedSO === order.id ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        {/* Expanded Details */}
        <Collapse in={expandedSO === order.id} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${order.status === "Approved"
                  ? "#4caf50"
                  : order.status === "Rejected"
                    ? "#f44336"
                    : "#ff9800"
                }`,
            }}
          >
            {/* Detailed Info - 2 Column Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              {/* Status */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  STATUS
                </Typography>
                <Chip
                  label={order.status}
                  color={
                    order.status === "Approved"
                      ? "success"
                      : order.status === "Rejected"
                        ? "error"
                        : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              {/* Division */}
              {/* <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  DIVISION
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                >
                  {order.division_name}
                </Typography>
              </Box> */}

              {/* Grand Total */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  GRAND TOTAL
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#2e7d32",
                  }}
                >
                  ₹{" "}
                  {order.grand_total
                    ? Number(order.grand_total).toLocaleString("en-IN", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })
                    : "0.0"}
                </Typography>
              </Box>

              {/* Amount Received */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  RECEIVED
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#1976d2",
                  }}
                >
                  ₹{" "}
                  {order.rec_amount
                    ? Number(order.rec_amount).toLocaleString("en-IN", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })
                    : "0.0"}
                </Typography>
              </Box>

              {/* Balance */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  BALANCE
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "#d32f2f",
                  }}
                >
                  ₹{" "}
                  {(
                    Number(order.grand_total) - Number(order.rec_amount)
                  ).toFixed(1)}
                </Typography>
              </Box>

              {/* Created By */}
              {order.created_by_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "#999",
                      letterSpacing: "0.5px",
                    }}
                  >
                    CREATED BY
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                  >
                    {order.created_by_name}
                  </Typography>
                </Box>
              )}

              {/* Approved By */}
              {order.approved_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "#999",
                      letterSpacing: "0.5px",
                    }}
                  >
                    APPROVED BY
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                  >
                    {order.approved_name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* QUOTATION NO */}
            <Box sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                QUOTATION NO
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: order.quotation_no ? "#1976d2" : "#999" }}>
                {order.quotation_no || order.quotation_ref_no || "-"}
              </Typography>
            </Box>

            {/* PO Number - Full Width */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                PO NUMBER
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "#555",
                  lineHeight: 1.3,
                }}
              >
                {order.po_number}
              </Typography>
            </Box>

            {/* Project Name - Compact */}
            {order.project_name && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  PROJECT
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#555",
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {order.project_name}
                </Typography>
              </Box>
            )}

            {/* Remarks - Only if exists */}
            {/* {order.remarks && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  REMARKS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#555",
                    fontStyle: "italic",
                    lineHeight: 1.3,
                  }}
                >
                  {order.remarks}
                </Typography>
              </Box>
            )} */}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            {/* Action Buttons - Compact */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Approve - Only Admin */}
              {userRole != "Staff" && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setApproveAnchorEl(true);
                    setSoToApprove(order);
                  }}
                  sx={{
                    bgcolor: "#e3f2fd",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": { bgcolor: "#bbdefb" },
                  }}
                >
                  <Visibility sx={{ fontSize: "1rem", color: "#1976d2" }} />
                </IconButton>
              )}
              {/* Action Icons - Compact */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 0.8,
                  mt: 1,
                  pt: 1,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                {/* View Project Button */}
                {order.project && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`${baseURL}${order.project}`, "_blank");
                    }}
                    sx={{
                      bgcolor: "#e3f2fd",
                      borderRadius: "6px",
                      width: 36,
                      height: 36,
                      "&:hover": { bgcolor: "#bbdefb" },
                    }}
                  >
                    <Visibility sx={{ fontSize: "1.1rem", color: "#1976d2" }} />
                  </IconButton>
                )}

                {/* Edit Button */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/dashboard/saleorder/${order.id}`);
                  }}
                  sx={{
                    bgcolor: "#e8f5e9",
                    borderRadius: "6px",
                    width: 36,
                    height: 36,
                    "&:hover": { bgcolor: "#c8e6c9" },
                  }}
                >
                  <Edit sx={{ fontSize: "1.1rem", color: "#4caf50" }} />
                </IconButton>

                {/* Delete Button */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setOrderToDelete(order.id);
                  }}
                  sx={{
                    bgcolor: "#ffebee",
                    borderRadius: "6px",
                    width: 36,
                    height: 36,
                    "&:hover": { bgcolor: "#ffcdd2" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: "1.1rem", color: "#f44336" }} />
                </IconButton>

                {/* Print Button */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    generatePdf(order);
                  }}
                  sx={{
                    bgcolor: "#fff3e0",
                    borderRadius: "6px",
                    width: 36,
                    height: 36,
                    "&:hover": { bgcolor: "#ffe0b2" },
                  }}
                >
                  <Print sx={{ fontSize: "1.1rem", color: "#ff9800" }} />
                </IconButton>

                {/* AMD Button (Admin Only) */}
                {/* {userRole != "Staff" && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      minWidth: 32,
                      height: 32,
                      padding: "0 8px",
                      fontSize: "0.65rem",
                      textTransform: "none",
                      backgroundColor: "#d26c19ff",
                      borderRadius: "6px",
                      "&:hover": {
                        backgroundColor: "#b85a15",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.2s",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/saleorder/${order.id}`, {
                        state: { amd: "AMD" },
                      });
                    }}
                  >
                    AMD
                  </Button>
                )} */}
              </Box>
            </Box>
          </Box>
        </Collapse>

        {/* Divider */}
        <Divider />
      </Box>
    );
  };

  return (
    <>
      {/* Filters */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        sx={{
          mt: { xs: 5, sm: -2 },
          pt: 0,
          mb: 1,
        }}
      >
        <Grid
          container
          spacing={1}
          alignItems="center"
          sx={{
            mt: -2,
            pt: 0,
            mb: 1,
          }}
        >
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                  customDateRange.from &&
                  customDateRange.to
                  ? `${formatDateDisplay(
                    customDateRange.from
                  )} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* Debtor */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Search By Debtor"
              name="party"
              value={filters.party}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.65rem",
                },
              }}
            />
          </Grid>

          {/* Division */}
          {/* <Grid item xs={12} sm={6} md={1.8}>
            <Autocomplete
              options={divisionLists || []}
              getOptionLabel={(opt) => opt?.division || ""}
              isOptionEqualToValue={(opt, val) =>
                opt.division_id === val?.division_id || opt.division_id === val
              }
              value={
                divisionLists.find(
                  (opt) => opt.division_id === filters.division
                ) || null
              }
              onChange={(e, newValue) =>
                setFilters((prev) => ({
                  ...prev,
                  division: newValue ? newValue.division_id : "",
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Division"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                    "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                  }}
                />
              )}
            />
          </Grid> */}

          {/* Project Name */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Search by Project Name"
              name="project_name"
              value={filters.project_name}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.65rem",
                },
              }}
            />
          </Grid>

          {/* PO Date */}
          <Grid item xs={12} sm={6} md={1.8}>
            <TextField
              label="Po Date"
              value={
                poDateRange.from && poDateRange.to
                  ? `${formatDateDisplay(
                    poDateRange.from
                  )} to ${formatDateDisplay(poDateRange.to)}`
                  : poDateRange.from
                    ? `From ${formatDateDisplay(poDateRange.from)}`
                    : poDateRange.to
                      ? `To ${formatDateDisplay(poDateRange.to)}`
                      : ""
              }
              placeholder="Select Po Date Range"
              onClick={(e) => {
                setPoDateMenuAnchor(e.currentTarget);
                setTempPoDateRange(poDateRange);
              }}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: "1 1 100px",
                cursor: "pointer",
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.65rem",
                },
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                },
              }}
            />
          </Grid>

          <Menu
            anchorEl={poDateMenuAnchor}
            open={Boolean(poDateMenuAnchor)}
            onClose={handleCancelPoDateRange}
            PaperProps={{
              sx: {
                width: 320,
                p: 0,
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#fafafa",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
              >
                Select Po Date Range
              </Typography>

              {/* From and To Date Inputs */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempPoDateRange.from}
                  onChange={(e) =>
                    setTempPoDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempPoDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempPoDateRange.to}
                  onChange={(e) =>
                    setTempPoDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempPoDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempPoDateRange.from && tempPoDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 2,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempPoDateRange.from)} to{" "}
                  {formatDateDisplay(tempPoDateRange.to)}
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={handleClearPoDateRange}
                  disabled={!poDateRange.from && !poDateRange.to}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                  }}
                >
                  Clear
                </Button>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelPoDateRange}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyPoDateRange}
                    disabled={!tempPoDateRange.from || !tempPoDateRange.to}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Menu>

          {/* Reset Button */}
          {/* Reset + Sales Order + Excel - Same Row on Mobile */}
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: { xs: "space-between", sm: "flex-start" },
              }}
            >
              {/* Reset Button */}
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleReset}
                sx={{
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                  flex: { xs: 1, sm: "none" },
                  minWidth: { sm: 70 },
                }}
              >
                Reset
              </Button>

              {/* Add Sales Order Button */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleClick}
                startIcon={<AddIcon sx={{ fontSize: "0.9rem !important" }} />}
                sx={{
                  height: 30,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                  flex: { xs: 1, sm: "none" },
                  minWidth: { sm: 120 },
                  whiteSpace: "nowrap",
                }}
              >
                Sales Order
              </Button>

              {/* Excel Export Button */}
              <IconButton
                onClick={handleExcelExport}
                disabled={isExcelLoading || salesOrders.length === 0}
                sx={{
                  height: 30,
                  width: 30,
                  minWidth: 30,
                  bgcolor: "success.main",
                  color: "white",
                  borderRadius: 0,
                  "&:hover": {
                    bgcolor: "success.dark",
                  },
                  "&:disabled": {
                    bgcolor: "grey.400",
                    color: "white",
                  },
                }}
              >
                {isExcelLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FileIcon sx={{ fontSize: "1rem" }} />
                )}
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* Date Range Menu - UPDATED */}
        <Menu
          anchorEl={dateMenuAnchor}
          open={Boolean(dateMenuAnchor)}
          onClose={() => {
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
            setTempCustomDateRange({ from: "", to: "" });
          }}
          PaperProps={{
            sx: {
              width: 350,
              maxHeight: 500,
              "& .MuiTypography-root": {
                fontSize: "0.7rem",
              },
            },
          }}
        >
          {[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "this_week", label: "This Week" },
            { key: "previous_week", label: "Previous Week" },
            { key: "this_month", label: "This Month" },
            { key: "current_year", label: "Current Year" },
            { key: "financial_year", label: "Financial Year" },
          ].map((range) => (
            <MenuItem
              key={range.key}
              onClick={() => {
                setFilters((prev) => ({ ...prev, date_range: range.key }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2">{range.label}</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {getDateRangeDisplay(range.key)}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          <Divider />

          {/* ✅ Custom Date Range - Expandable */}
          <MenuItem
            onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
            >
              Custom Date Range
            </Typography>
            {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
          </MenuItem>

          {/* ✅ Both Date Pickers Together */}
          <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Select Date Range
              </Typography>

              {/* From and To Date Inputs Side by Side */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.from}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempCustomDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.to}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempCustomDateRange.from && tempCustomDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1.5,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                  {formatDateDisplay(tempCustomDateRange.to)}
                </Typography>
              )}

              {/* Apply and Cancel Buttons */}
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelCustomDateRange}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyCustomDateRange}
                  disabled={
                    !tempCustomDateRange.from || !tempCustomDateRange.to
                  }
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: 1,
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Menu>
      </Box>
      <Box sx={{ position: "relative", flex: 1, minHeight: "400px" }}>

        {/* ✅ LOADING OVERLAY - Only shows for filter changes */}
        {filterLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              zIndex: 1000,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={50} thickness={4} color="primary" />
            <Typography sx={{ mt: 2, color: "#333", fontWeight: 500 }}>
              {filters.date_range === "financial_year"
                ? "Loading Financial Year Data..."
                : filters.date_range === "current_year"
                  ? "Loading Current Year Data..."
                  : filters.date_range === "this_month"
                    ? "Loading This Month Data..."
                    : filters.date_range === "this_week"
                      ? "Loading This Week Data..."
                      : filters.date_range === "previous_week"
                        ? "Loading Previous Week Data..."
                        : filters.date_range === "custom"
                          ? "Loading Custom Date Range..."
                          : filters.po_date_from && filters.po_date_to
                            ? "Loading PO Date Range..."
                            : "Loading Data..."}
            </Typography>
            <Typography sx={{ mt: 0.5, color: "#666", fontSize: "0.8rem" }}>
              Please wait, this may take a moment
            </Typography>
          </Box>
        )}


        {/* Main Content */}
        <InfiniteScroll
          dataLength={salesOrders.length}
          next={fetchMoreData}
          hasMore={hasMore}
          // height={isMobile ? "75vh" : "88vh"}
          loader={
            <Typography textAlign="center" p={2}>
              Loading...
            </Typography>
          }
          endMessage={
            <Typography textAlign="center" p={2}>
              No more records!
            </Typography>
          }
        >
          {/* Mobile View */}
          {/* {isMobile ? (
          <Box sx={{ px: 1 }}>
            {salesOrders.map((order, index) => (
              <MobileSalesOrderCard key={order.id} order={order} index={index} />
            ))}
          </Box> */}
          {isMobile ? (
            // ✅ Mobile View - WITHOUT Card wrapper
            <Box sx={{ bgcolor: "background.paper" }}>
              {salesOrders.map((order, index) => (
                <MobileSalesOrderCard
                  key={order.id}
                  order={order}
                  index={index}
                />
              ))}
            </Box>
          ) : (
            /* Desktop Table View */
            <TableContainer
              component={Paper}
              sx={{ maxHeight: "88vh", overflow: "auto" }}
            >
              <Table
                hover
                stickyHeader
                size="small"
                sx={{
                  minWidth: "max-content",
                  "& td, & th": {
                    borderBottom: "1px solid #ccc",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "#2196f3" : "#333",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                    },
                  }}
                >
                  <TableRow>
                    {userRole != "Staff" && <TableCell>View</TableCell>}
                    <TableCell align="center">S.No.</TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        minWidth: 100,
                        backgroundColor: "#fff",
                        zIndex: 3,
                      }}
                      align="center"
                    >
                      So. No.
                    </TableCell>
                    <TableCell align="center">Quotation No</TableCell>
                    {/* <TableCell align="center">Status</TableCell> */}
                    <TableCell align="center">Debtor</TableCell>
                    <TableCell align="center">PO No</TableCell>
                    <TableCell align="center">PO Date</TableCell>
                    <TableCell align="center">Division</TableCell>
                    <TableCell align="center">Grand Total</TableCell>
                    <TableCell align="center">Project</TableCell>
                    {/* <TableCell align="center">Ad Rec</TableCell> */}
                    {/* <TableCell align="center">Bal Amt</TableCell> */}
                    {/* <TableCell align="center">Approved By</TableCell> */}
                    <TableCell align="center">Created By</TableCell>
                    <TableCell align="center">Date</TableCell>
                    {/* <TableCell align="center">Remarks</TableCell> */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        minWidth: 140,
                        backgroundColor: "#fff",
                        zIndex: 3,
                      }}
                      align="center"
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {salesOrders.map((order, idx) => {
                    const defaultBg = idx % 2 === 0 ? "#fafafa" : "#ffffff";
                    const hoverBg = "#e3f2fd";
                    return (
                      <TableRow
                        hover
                        key={idx}
                        sx={{
                          backgroundColor: defaultBg,
                          // "&:hover": {
                          //   backgroundColor: hoverBg,
                          "& td": {
                            // backgroundColor: hoverBg,
                            paddingRight: "13px !important",
                            paddingLeft: "13px !important",
                            padding: "0px",
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            whiteSpace: "nowrap",
                          },
                          // },
                        }}
                      >
                        {userRole != "Staff" && (
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setApproveAnchorEl(true);
                                setSoToApprove(order);
                              }}
                              sx={{ padding: 0 }}
                            >
                              <Visibility sx={{ fontSize: "1rem", color: "#1976d2" }} />
                            </IconButton>
                          </TableCell>
                        )}

                        <TableCell align="center">{idx + 1}</TableCell>

                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            minWidth: 100,
                            backgroundColor: defaultBg,
                            zIndex: 2,
                          }}
                          align="center"
                        >
                          {order.so_no}
                        </TableCell>

                        <TableCell align="center">
                          {order.quotation_no ||
                            order.quotation_ref_no ||
                            (order.quotation ? `#${order.quotation}` : "-")}
                        </TableCell>
                        {/* <TableCell align="center">
                        <Chip
                          label={
                            order.status === "Approved"
                              ? "Approved"
                              : order.status === "Rejected"
                              ? "Rejected"
                              : "Pending"
                          }
                          color={
                            order.status === "Approved"
                              ? "success"
                              : order.status === "Rejected"
                              ? "error"
                              : "warning"
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            // fontSize: "0.2rem", // 👈 font chhota hoga
                            height: "10px",
                            transform: "scale(0.8)",
                            "& .MuiChip-label": {
                              padding: "0 6px",
                            },
                          }}
                        />
                      </TableCell> */}
                        <TableCell align="center">
                          <Tooltip
                            title={order.party_details?.company_name}
                            arrow
                          >
                            <span>
                              {truncateText(
                                order.party_details?.company_name,
                                20
                              )}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={order.po_number} arrow>
                            <span>{truncateText(order.po_number, 30)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          {new Date(order.order_date).toLocaleDateString("en-GB")}
                        </TableCell>
                        <TableCell align="center">
                          {order.division_name}
                        </TableCell>
                        <TableCell align="center">
                          {order.grand_total
                            ? Number(order.grand_total).toLocaleString("en-IN", {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })
                            : "0.00"}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={order.project_name} arrow>
                            <span>{truncateText(order.project_name, 30)}</span>
                          </Tooltip>
                        </TableCell>

                        {/* <TableCell align="center">
                        {order.rec_amount
                          ? Number(order.rec_amount).toLocaleString("en-IN", {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })
                          : "0.00"}
                      </TableCell> */}
                        {/* <TableCell align="center">
                        {(
                          Number(order.grand_total) - Number(order.rec_amount)
                        ).toFixed(1)}
                      </TableCell> */}

                        {/* <TableCell align="center">
                        {order.approved_name}
                      </TableCell> */}
                        <TableCell align="center">
                          {order.created_by_name}
                        </TableCell>
                        <TableCell align="center">
                          {formatOnlyDate(order.created_at)}
                        </TableCell>
                        {/* <TableCell align="center">
                        <Tooltip title={order.remarks} arrow>
                          <span>{truncateText(order.remarks, 20)}</span>
                        </Tooltip>
                      </TableCell> */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            minWidth: 140,
                            backgroundColor: defaultBg,
                            zIndex: 2,
                          }}
                          align="center"
                        >
                          <span style={{ display: "inline-block", width: 40 }}>
                            {order.project ? (
                              <IconButton
                                sx={{
                                  padding: 0,
                                  paddingLeft: 1,
                                  paddingRight: 1,
                                }}
                                size="small"
                                color="info"
                                onClick={() =>
                                  window.open(`${baseURL}${order.project}`, "_blank")
                                }
                              >
                                <Visibility transform="scale(0.6)" />
                              </IconButton>
                            ) : (
                              <span
                                style={{ display: "inline-block", width: 40 }}
                              />
                            )}
                          </span>
                          <IconButton
                            sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                            onClick={() =>
                              navigate(`/dashboard/saleorder/${order.id}`)
                            }
                            // disabled={order.status === "Approved"}
                            size="small"
                            color="primary"
                          >
                            <Edit transform="scale(0.6)" />
                          </IconButton>
                          <IconButton
                            sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                            color="error"
                            size="small"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setOrderToDelete(order.id);
                            }}
                          // disabled={order.status === "Approved"}
                          >
                            <DeleteIcon transform="scale(0.6)" />
                          </IconButton>
                          <IconButton
                            sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                            size="small"
                            color="default"
                            onClick={() => generatePdf(order)}
                          >
                            <Print transform="scale(0.6)" />
                          </IconButton>
                          {/* {localStorage.getItem("role") != "Staff" && (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              minWidth: "35px",
                              height: "20px",
                              padding: "0 7px",
                              fontSize: "0.6rem",
                              textTransform: "none",
                              backgroundColor: "#d26c19ff",
                              transform: "scale(0.8)",
                            }}
                            onClick={() =>
                              navigate(`/dashboard/saleorder/${order.id}`, {
                                state: {
                                  amd: "AMD",
                                },
                              })
                            }
                          >
                            AMD
                          </Button>
                        )} */}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </Box>

      <ToastContainer position="top-right" autoClose={3000} />

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setOrderToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete this sales order?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                await handleDelete(orderToDelete);
                setAnchorEl(null);
                setOrderToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setOrderToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Approve/Reject Popover */}
      <Dialog
        open={Boolean(approveAnchorEl)}
        onClose={() => {
          setApproveAnchorEl(null);
          setSoToApprove(null);
          setRemarkInput("");
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderRadius: 2,
            width: "fit-content", // ✅ Prevents extra space
            maxWidth: "95vw", // ✅ Ensures it doesn't overflow on small screens
          },
        }}
      >
        {soToApprove && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* Scrollable Content */}
            <Box
              sx={{
                overflowY: "auto",
                p: 2,
                bgcolor: "#f5f5f5",
                maxHeight: "calc(90vh - 100px)", // ✅ Leaves space for footer
              }}
            >
              <SalesOrderPreview order={soToApprove} />
            </Box>

            {/* Fixed Footer - Single Row Layout */}
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #ddd",
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                flexWrap: "wrap", // ✅ Responsive on mobile
              }}
            >
              {/* Remarks Input - Takes available space */}
              {/* <TextField
                size="small"
                label="Remarks *"
                variant="outlined"
                value={remarkInput}
                onChange={(e) => setRemarkInput(e.target.value)}
                placeholder="Enter approval or rejection remarks here..."
                required
                disabled={
                  soToApprove.status === "Approved" ||
                  soToApprove.status === "Rejected"
                }
                sx={{
                  flex: 1,
                  minWidth: "300px",
                }}
              /> */}

              {/* Buttons Group */}
              <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  color="error"
                  size="small"
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setSoToApprove(null);
                    setRemarkInput("");
                  }}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Close
                </Button>
                {/* Edit Button */}
                {/* <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  color="primary"
                  size="small"
                  disabled={
                    soToApprove.status === "Approved" ||
                    soToApprove.status === "Rejected"
                  }
                  onClick={() => {
                    navigate(`/dashboard/saleorder/${soToApprove.id}`);
                    setApproveAnchorEl(null);
                  }}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Edit
                </Button> */}

                {/* Reject Button */}
                {/* <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={
                    soToApprove.status === "Approved" ||
                    soToApprove.status === "Rejected"
                  }
                  onClick={() => {
                    if (!remarkInput.trim()) {
                      toast.error("Remark is required for rejection!");
                      return;
                    }
                    handleApprove({
                      ...soToApprove,
                      status: "Rejected",
                      remarks: remarkInput,
                    });
                    setApproveAnchorEl(null);
                    setRemarkInput("");
                  }}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reject
                </Button> */}

                {/* Approve Button */}
                {/* <Button
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={
                    soToApprove.status === "Approved" ||
                    soToApprove.status === "Rejected"
                  }
                  onClick={() => {
                    if (!remarkInput.trim()) {
                      toast.error("Remark is required for approval!");
                      return;
                    }
                    handleApprove({
                      ...soToApprove,
                      status: "Approved",
                      remarks: remarkInput,
                    });
                    setApproveAnchorEl(null);
                    setRemarkInput("");
                  }}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Approve
                </Button> */}
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>
    </>
  );
}
